// Phase 6C · AI Studio Drafts standalone page.
// Route: /dashboard/ai-studio/drafts
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import AIStudioComposer from "@/components/dashboard/AIStudioComposer";
import {
  listDrafts, getDraft, regenerateDraft, approveDraft, deleteDraft,
} from "@/lib/aiStudioDrafts";

function StatusBadge({ status }) {
  const cls = status === "approved"
    ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-700";
  return <Badge data-testid={`draft-status-${status}`} className={cls}>{status}</Badge>;
}

export default function AIStudioDraftsPage() {
  const [drafts, setDrafts] = useState([]);
  const [detail, setDetail] = useState(null);

  const refresh = useCallback(async () => {
    try { setDrafts(await listDrafts()); } catch {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const open = async (id) => {
    try { setDetail(await getDraft(id)); } catch { toast.error("Not found"); }
  };
  const regen = async (id) => {
    try {
      const d = await regenerateDraft(id);
      setDetail(d); refresh(); toast.success("New version created");
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Regen failed"); }
  };
  const approve = async (id) => {
    try {
      const d = await approveDraft(id);
      setDetail(d); refresh(); toast.success("Draft approved");
    } catch (e) { toast.error("Approve failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Soft-delete this draft?")) return;
    try { await deleteDraft(id); setDetail(null); refresh(); } catch {}
  };

  return (
    <div className="space-y-6 p-6" data-testid="ai-studio-drafts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold"><span className="notranslate" translate="no">AI Studio</span> · Drafts</h1>
          <p className="text-sm text-neutral-500">
            Compose, version and approve AI drafts. Approval is internal — no
            email, no publish.
          </p>
        </div>
        <AIStudioComposer
          sourceEntityType="manual"
          triggerLabel="New draft"
          onCreated={() => refresh()}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your drafts</CardTitle>
          <CardDescription>Workspace-scoped. Versions are append-only.</CardDescription>
        </CardHeader>
        <CardContent>
          {drafts.length === 0 ? (
            <p className="text-sm text-neutral-500">No drafts yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Versions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drafts.map((d) => (
                  <TableRow key={d.id} data-testid={`draft-row-${d.id}`}>
                    <TableCell className="font-medium">{d.title}</TableCell>
                    <TableCell>{d.kind}</TableCell>
                    <TableCell className="text-xs text-neutral-500">
                      {d.source_entity_type}
                    </TableCell>
                    <TableCell>{d.versions?.length || 0}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`draft-open-${d.id}`}
                        size="sm" variant="outline" onClick={() => open(d.id)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="draft-detail" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detail?.title}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <StatusBadge status={detail.status} />
                <span>{detail.kind}</span>
                <span>· source: {detail.source_entity_type}</span>
                <span>· versions: {detail.versions.length}</span>
              </div>
              <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                {detail.versions.map((v) => (
                  <div
                    key={v.v}
                    data-testid={`draft-version-${v.v}`}
                    className="border border-neutral-200 rounded-md p-3"
                  >
                    <div className="text-xs text-neutral-500 mb-1">
                      v{v.v} · {v.provider}/{v.model}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">
                      {v.output_ref}
                    </div>
                    {v.brand_kit_snapshot && (
                      <div className="mt-2 text-[11px] text-neutral-500">
                        Brand snapshot: tone={v.brand_kit_snapshot.tone} ·
                        voice={v.brand_kit_snapshot.voice}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            {detail?.status === "draft" && (
              <>
                <Button
                  data-testid="draft-regen-btn"
                  variant="outline" onClick={() => regen(detail.id)}
                >
                  Regenerate
                </Button>
                <Button
                  data-testid="draft-approve-btn"
                  onClick={() => approve(detail.id)}
                >
                  Approve (internal)
                </Button>
              </>
            )}
            <Button
              data-testid="draft-delete-btn"
              variant="destructive" onClick={() => del(detail.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
